import { createAsyncThunk, createSelector, createSlice, PayloadAction, ThunkDispatch } from '@reduxjs/toolkit';
import type { ChatMessage, Conversation, MessageFeedback, SkubotChatsState } from '../types/skubot';
import { StoreState } from '../reducers/core';
import { skubotApi } from '../components/skubot/lib/skubotApi';
import { generateUUID } from '../utils';
import { AnyAction } from 'redux';
import { getErrorMessageFromAction, getMessageFromResponseError } from './utils';

const initialState: SkubotChatsState = {
    conversations: {},
    messages: {},
    activeConversationId: null,
    loading: false,
    errors: {},
};


const fetchConversations = createAsyncThunk<Conversation[], void>('skubot/fetchConversations', async (_, thunkAPI) => {
    try {
        const conversations = await skubotApi.getConversations();
        return conversations;
    } catch (error) {
        return thunkAPI.rejectWithValue(getMessageFromResponseError(error));
    }
});

const updateConversationTitle = createAsyncThunk<Conversation, { conversationId: string, title: string }>('skubot/updateConversationTitle', async ({ conversationId, title }, thunkAPI) => {

    try {
        const conversation = await skubotApi.updateConversationTitle(conversationId, title);
        return conversation;
    } catch (error) {
        return thunkAPI.rejectWithValue(getMessageFromResponseError(error));
    }
});

const deleteConversation = createAsyncThunk<void, string>('skubot/deleteConversation', async (conversationId, thunkAPI) => {
    try {
        await skubotApi.deleteConversation(conversationId);
        return;
    } catch (error) {
        return thunkAPI.rejectWithValue(getMessageFromResponseError(error));
    }
});

const fetchConversationMessages = createAsyncThunk<ChatMessage[], string>('skubot/fetchConversationMessages', async (conversationId, thunkAPI) => {
    try {
        const messages = await skubotApi.getConversationMessages(conversationId);
        return messages;
    } catch (error) {
        return thunkAPI.rejectWithValue(getMessageFromResponseError(error));
    }
});

const chat = createAsyncThunk<ChatMessage, { conversationId: string, message: string, id: string }>('skubot/chat', async ({ conversationId, message, id }, thunkAPI) => {
    try {
        const messageResponse = await skubotApi.chat(conversationId, message, id);
        return messageResponse;
    } catch (error) {
        return thunkAPI.rejectWithValue(getMessageFromResponseError(error));
    }
});


const ERROR_KEYS = {
    MESSAGE_ERROR: 'MESSAGE_ERROR',
    CONVERSATION_ERROR: 'CONVERSATION_ERROR',
    MESSAGE_FEEDBACK_ERROR: 'MESSAGE_FEEDBACK_ERROR',
};

const skubotSlice = createSlice({
    name: 'skubot',
    initialState,
    reducers: {
        setConversations(state, action: PayloadAction<{ [key: string]: Conversation }>) {
            state.conversations = action.payload;
        },
        addConversation(state, action: PayloadAction<Conversation>) {
            state.conversations[action.payload.sessionId] = action.payload;
        },
        setMessages(state, action: PayloadAction<{ [key: string]: ChatMessage }>) {
            state.messages = action.payload;
        },
        setActiveConversationId(state, action: PayloadAction<string | null>) {
            state.activeConversationId = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setErrors(state, action: PayloadAction<{ [key: string]: string | null }>) {
            state.errors = action.payload;
        },
        setMessageError(state, action: PayloadAction<string | null>) {
            state.errors[ERROR_KEYS.MESSAGE_ERROR] = action.payload;
        },
        setConversationError(state, action: PayloadAction<string | null>) {
            state.errors[ERROR_KEYS.CONVERSATION_ERROR] = action.payload;
        },
        setMessageFeedbackError(state, action: PayloadAction<string | null>) {
            state.errors[ERROR_KEYS.MESSAGE_FEEDBACK_ERROR] = action.payload;
        },
        addMessage(state, action: PayloadAction<ChatMessage>) {
            state.messages[action.payload.id] = action.payload;
        },
        setMessageFeedback(
            state,
            action: PayloadAction<{ id: string; feedback: MessageFeedback | null; feedbackComment?: string | null }>,
        ) {
            const message = state.messages[action.payload.id];
            if (message) {
                message.feedback = action.payload.feedback;
                message.feedbackComment = action.payload.feedbackComment ?? null;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConversations.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchConversations.fulfilled, (state, action) => {
                state.conversations = action.payload.reduce((acc, conversation) => {
                    acc[conversation.sessionId] = conversation;
                    return acc;
                }, {} as { [key: string]: Conversation });
                state.loading = false;
                state.errors[ERROR_KEYS.CONVERSATION_ERROR] = null;
            })
            .addCase(fetchConversations.rejected, (state, action) => {
                state.errors[ERROR_KEYS.CONVERSATION_ERROR] = getErrorMessageFromAction(action, "Failed to fetch conversations");
                state.loading = false;
            })

            .addCase(updateConversationTitle.fulfilled, (state, action) => {
                state.conversations[action.payload.sessionId] = action.payload;
                state.errors[ERROR_KEYS.CONVERSATION_ERROR] = null;
            })
            .addCase(updateConversationTitle.rejected, (state, action) => {
                state.errors[ERROR_KEYS.CONVERSATION_ERROR] = getErrorMessageFromAction(action, "Failed to update conversation title");
            })

            .addCase(deleteConversation.fulfilled, (state, action) => {
                delete state.conversations[action.meta.arg];
                if (state.activeConversationId === action.meta.arg) {
                    state.activeConversationId = null;
                }
                state.errors[ERROR_KEYS.CONVERSATION_ERROR] = null;
            })
            .addCase(deleteConversation.rejected, (state, action) => {
                state.errors[ERROR_KEYS.CONVERSATION_ERROR] = getErrorMessageFromAction(action, "Failed to delete conversation");
            })

            .addCase(fetchConversationMessages.pending, (state) => {
                state.loading = true;
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = null;
            })
            .addCase(fetchConversationMessages.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = action.payload.reduce((acc: { [key: string]: ChatMessage }, message) => {
                    acc[message.id] = message;
                    return acc;
                }, {});
                state.errors = {};
            })
            .addCase(fetchConversationMessages.rejected, (state, action) => {
                state.loading = false;
                state.errors = {
                    [ERROR_KEYS.MESSAGE_ERROR]: getErrorMessageFromAction(action, "Failed to fetch conversation messages"),
                };
            })

            .addCase(chat.pending, (state) => {
                state.loading = true;
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = null;
            })
            .addCase(chat.fulfilled, (state, action) => {
                state.activeConversationId = action.payload.conversationId;
                state.messages[action.payload.id] = action.payload;
                state.loading = false;
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = null;
            })
            .addCase(chat.rejected, (state, action) => {
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = getErrorMessageFromAction(action, "skubot's response could not be fully generated.");
                delete state.messages[action.meta.arg.id];
                state.loading = false;
            });
    },
});

export {
    fetchConversations,
    updateConversationTitle,
    deleteConversation,
    fetchConversationMessages,
    chat,
};

export const {
    setConversations,
    setMessages,
    setActiveConversationId,
    setLoading,
    setErrors,
    setMessageError,
    setConversationError,
    setMessageFeedbackError,
    addMessage,
    setMessageFeedback,
    addConversation,
} = skubotSlice.actions;

export const createNewConversation = (message?: string) => async (dispatch: ThunkDispatch<StoreState, undefined, AnyAction>, getState: () => StoreState) => {
    let sessionId: string;
    try {
        sessionId = await skubotApi.generateSessionId();
    } catch (error) {
        sessionId = generateUUID();
    }

    const conversation = {
        id: 'temp-' + sessionId,
        sessionId: sessionId,
        title: message ? message.substring(0, 200) + (message.length > 200 ? '...' : '') : 'New Conversation',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };

    dispatch(addConversation(conversation));
    dispatch(setActiveConversationId(conversation.sessionId));
    return conversation;
};

export const sendChatMessage = (message: string) => async (dispatch: ThunkDispatch<StoreState, undefined, AnyAction>, getState: () => StoreState) => {
    dispatch(setMessageError(null));
    dispatch(setConversationError(null));
    dispatch(setMessageFeedbackError(null));

    let activeConversationId = selectActiveConversationId(getState());
    if (!activeConversationId) {
        const conversation = await dispatch(createNewConversation(message));
        activeConversationId = conversation.sessionId;
    }
    const assistantMessageId = generateUUID();
    const userMessageId = generateUUID();

    dispatch(addMessage({
        id: userMessageId,
        conversationId: activeConversationId,
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
    }));
    dispatch(addMessage({
        id: assistantMessageId,
        conversationId: activeConversationId,
        role: 'assistant',
        content: 'Thinking...',
        createdAt: new Date().toISOString(),
    }));
    dispatch(chat({ conversationId: activeConversationId, message: message, id: assistantMessageId }));
};

export const submitFeedback = (
    { messageId, feedback, comment }: { messageId: string; feedback: MessageFeedback; comment?: string },
) => async (dispatch: ThunkDispatch<StoreState, undefined, AnyAction>, getState: () => StoreState) => {
    dispatch(setMessageFeedbackError(null));

    const message = getState().skubot.messages[messageId];
    if (!message) {
        dispatch(setMessageFeedbackError('Message not found'));
        return;
    }
    if (!message.traceId) {
        dispatch(setMessageFeedbackError('Message does not have a trace ID'));
        return;
    }

    const previous = getState().skubot.messages[messageId];
    const previousFeedback = previous?.feedback ?? null;
    const previousComment = previous?.feedbackComment ?? null;

    dispatch(setMessageFeedback({ id: messageId, feedback, feedbackComment: comment ?? null }));
    try {
        await skubotApi.submitFeedback(
            message.traceId,
            feedback === 'positive' ? 'up' : 'down',
            comment,
        );
    } catch (error) {
        const current = getState().skubot.messages[messageId];
        if (current && current.feedback === feedback && current.feedbackComment === (comment ?? null)) {
            dispatch(setMessageFeedback({ id: messageId, feedback: previousFeedback, feedbackComment: previousComment }));
        }
        dispatch(setMessageFeedbackError(error instanceof Error ? error.message : 'Failed to submit feedback'));
    }
};


export const selectConversations = (state: StoreState) => state.skubot.conversations;
export const selectMessages = (state: StoreState) => state.skubot.messages;
export const selectActiveConversationId = (state: StoreState) => state.skubot.activeConversationId;
export const selectLoading = (state: StoreState) => state.skubot.loading;
export const selectErrors = (state: StoreState) => state.skubot.errors;
export const selectMessageError = (state: StoreState) => state.skubot.errors[ERROR_KEYS.MESSAGE_ERROR] ?? null;
export const selectConversationError = (state: StoreState) => state.skubot.errors[ERROR_KEYS.CONVERSATION_ERROR] ?? null;
export const selectMessageFeedbackError = (state: StoreState) => state.skubot.errors[ERROR_KEYS.MESSAGE_FEEDBACK_ERROR] ?? null;

export const selectConversationsList = createSelector(
    selectConversations,
    (conversations) => Object.values(conversations).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
);

export const selectActiveConversation = createSelector(
    selectConversations,
    selectActiveConversationId,
    (conversations, activeConversationId) => activeConversationId ? conversations[activeConversationId] : undefined
);

export const selectMessagesForConversation = createSelector(
    selectMessages,
    selectActiveConversationId,
    (messages, activeConversationId) => (
        activeConversationId
            ? Object.values(messages).filter(message => message.conversationId === activeConversationId)
            : []
    ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
);

export default skubotSlice.reducer;
